import {
  evaluateUploadFormula,
  type UploadFormula,
  type UploadFormulaCell,
} from '@erp/shared-utils';
import type { UploadColumnMapping, UploadSourceTransform } from '@erp/rpc';

import {
  as400Date,
  cellDecimal,
  cellText,
  roundTo,
  type ResultsCell,
} from './sheetCells';

/**
 * One upload column's value for one input row. Column flags (`Y` when another
 * upload column is filled) and row numbers depend on the whole line, so they
 * are resolved by the caller; this handles everything read from the row.
 */

export type CellProblem =
  'blank' | 'not-a-number' | 'bad-date' | 'divide-by-zero' | 'formula-text';

export type MappedValue =
  | { ok: true; value: string }
  | { ok: false; problem: CellProblem; header?: string; value: string };

/** Reads the current row's cell under a header. */
export type RowReader = (header: string) => ResultsCell;

/** A layout mapping with its formula already parsed. */
export type CompiledMapping =
  | Exclude<UploadColumnMapping, { kind: 'formula' }>
  | (Extract<UploadColumnMapping, { kind: 'formula' }> & {
      formula: UploadFormula;
    });

function ok(value: string): MappedValue {
  return { ok: true, value };
}

/**
 * Text a spreadsheet would run as a formula. Such a value is refused, never
 * escaped: the bytes AS400 receives must be the values as written.
 */
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/** Text written as is, refused when it would run as a spreadsheet formula. */
function guardedText(value: string, header?: string): MappedValue {
  return FORMULA_PREFIX.test(value)
    ? { ok: false, problem: 'formula-text', header, value }
    : ok(value);
}

function sourceValue(
  cell: ResultsCell,
  header: string,
  transform: UploadSourceTransform,
  date1904: boolean,
): MappedValue {
  const raw = cellText(cell);
  if (raw === '') return ok('');
  // A number cell (e.g. -9.5) is a value, not text a spreadsheet would run.
  const guard =
    typeof cell === 'string' ? guardedText : (value: string) => ok(value);
  if (transform.type === 'text') return guard(raw, header);
  if (transform.type === 'substring') {
    const end =
      transform.length === null
        ? undefined
        : transform.start + transform.length;
    return guard(raw.slice(transform.start, end), header);
  }
  if (transform.type === 'date') {
    const date = as400Date(cell, date1904);
    return date === null
      ? { ok: false, problem: 'bad-date', header, value: raw }
      : ok(date);
  }
  const number = cellDecimal(cell);
  return number === null
    ? { ok: false, problem: 'not-a-number', header, value: raw }
    : ok(roundTo(number, transform.decimals));
}

function formulaCell(cell: ResultsCell): UploadFormulaCell {
  if (typeof cell === 'number' || typeof cell === 'string') return cell;
  return cellText(cell);
}

function formulaValue(
  mapping: Extract<CompiledMapping, { kind: 'formula' }>,
  row: RowReader,
): MappedValue {
  const result = evaluateUploadFormula(mapping.formula, (header) =>
    formulaCell(row(header)),
  );
  if (!result.ok) {
    if (result.reason === 'divide-by-zero') {
      return { ok: false, problem: 'divide-by-zero', value: '' };
    }
    return {
      ok: false,
      problem: result.reason,
      header: result.header,
      value: cellText(row(result.header)),
    };
  }
  if (mapping.blankWhenZero && result.value.isZero()) return ok('');
  return ok(roundTo(result.value, mapping.decimals));
}

/** Every mapping but row numbers and column flags, which need the whole line. */
export function mapColumn(
  mapping: CompiledMapping,
  row: RowReader,
  date1904: boolean,
): MappedValue {
  switch (mapping.kind) {
    case 'constant':
      return guardedText(mapping.value);
    case 'rowNumber':
      return ok('');
    case 'source':
      return sourceValue(
        row(mapping.header),
        mapping.header,
        mapping.transform,
        date1904,
      );
    case 'flag':
      if ('column' in mapping.when) return ok('');
      return ok(cellText(row(mapping.when.header)) === '' ? '' : mapping.value);
    case 'formula':
      return formulaValue(mapping, row);
  }
}
